import { Building2, Handshake, Wallet } from 'lucide-react'
import { useMemo } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { NumberInput } from '@/components/ui/number-input'
import { Select } from '@/components/ui/select'
import { TimeField } from '@/components/ui/time-field'
import { useDepartmentPositions, useHrDepartments } from '../api/referenceApi'
import { FormField } from '../components/FormField'
import { usePersonelList } from '../hooks/usePersonelStore'
import { CALISMA_TURU_VALUES, DURUM_VALUES, ISTIHDAM_VALUES, SIGORTA_VALUES, type PersonelFormValues } from '../schemas/personelFormSchema'
import { FormSection } from './FormBits'
import { useFieldError } from './useFieldError'

export function IsFormTab() {
  const { t } = useTranslation()
  const { register, control, setValue } = useFormContext<PersonelFormValues>()
  const sigorta = useWatch({ control, name: 'sigorta' })
  const ortak = useWatch({ control, name: 'ortak' })
  const err = useFieldError()
  const depsQ = useHrDepartments()
  const list = usePersonelList()
  const [departmanId, ad] = useWatch({ control, name: ['departman_id', 'ad'] })
  const posQ = useDepartmentPositions(departmanId)
  const managers = useMemo(
    () =>
      list
        .filter((p) => p.ad !== ad)
        .map((p) => ({ value: p.id, label: p.ad, description: [p.pozisyon, p.departman].filter(Boolean).join(' · ') }))
        .sort((a, b) => a.label.localeCompare(b.label, 'tr')),
    [list, ad],
  )
  const time = (name: 'calisma_baslangic' | 'calisma_bitis', id: string, label: string) => (
    <FormField label={label} htmlFor={id} required error={err(name)}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <TimeField id={id} className="h-10 rounded-xl" value={field.value} onChange={field.onChange} aria-invalid={!!err(name)} aria-label={label} />
        )}
      />
    </FormField>
  )

  return (
    <div className="space-y-4">
      <FormSection title={t('hr.personel.genel.organizasyon')} icon={<Building2 />}>
        <FormField label={t('hr.personel.fields.sicil')} htmlFor="f-sicil" error={err('sicil')}>
          <Input id="f-sicil" className="h-10 rounded-xl" readOnly placeholder={t('hr.personel.form.sicilAuto')} {...register('sicil')} />
        </FormField>
        <FormField label={t('hr.personel.columns.durum')} required error={err('durum')}>
          <Controller
            control={control}
            name="durum"
            render={({ field }) => (
              <Select
                size="lg"
                value={field.value}
                onChange={field.onChange}
                aria-label={t('hr.personel.columns.durum')}
                options={DURUM_VALUES.map((v) => ({ value: v, label: t(`hr.personel.durum.${v}`) }))}
              />
            )}
          />
        </FormField>
        <FormField label={t('hr.personel.fields.departman')} required error={err('departman_id')}>
          <Controller
            control={control}
            name="departman_id"
            render={({ field }) => (
              <Select
                searchable
                size="lg"
                className="rounded-xl"
                value={field.value}
                onChange={(v) => {
                  field.onChange(v)
                  setValue('pozisyon', '', { shouldDirty: true })
                }}
                options={(depsQ.data ?? []).map((d) => ({ value: d.id, label: d.name }))}
                loading={depsQ.isLoading}
                placeholder={t('hr.personel.form.selectDepartment')}
                searchPlaceholder={t('hr.personel.form.searchDepartment')}
                aria-label={t('hr.personel.fields.departman')}
              />
            )}
          />
        </FormField>
        <FormField label={t('hr.personel.fields.pozisyon')} required error={err('pozisyon')}>
          <Controller
            control={control}
            name="pozisyon"
            render={({ field }) => (
              <Select
                searchable
                size="lg"
                className="rounded-xl"
                value={field.value}
                onChange={field.onChange}
                options={posQ.data.map((p) => ({ value: p.id, label: p.name }))}
                loading={posQ.isLoading}
                disabled={!departmanId}
                placeholder={t('hr.personel.form.selectPosition')}
                aria-label={t('hr.personel.fields.pozisyon')}
              />
            )}
          />
        </FormField>
        <FormField label={t('hr.personel.fields.yonetici')} error={err('yonetici')}>
          <Controller
            control={control}
            name="yonetici"
            render={({ field }) => (
              <Select
                searchable
                size="lg"
                className="rounded-xl"
                value={field.value}
                onChange={field.onChange}
                options={managers}
                placeholder={t('hr.personel.form.selectManager')}
                searchPlaceholder={t('hr.personel.form.searchManager')}
                aria-label={t('hr.personel.fields.yonetici')}
              />
            )}
          />
        </FormField>
        <FormField label={t('hr.personel.fields.sube')} htmlFor="f-sube" required error={err('sube')}>
          <Input id="f-sube" className="h-10 rounded-xl" {...register('sube')} />
        </FormField>
        <FormField label={t('hr.personel.fields.istihdam')} required error={err('istihdam')}>
          <Controller
            control={control}
            name="istihdam"
            render={({ field }) => (
              <Select
                size="lg"
                value={field.value}
                onChange={field.onChange}
                aria-label={t('hr.personel.fields.istihdam')}
                options={ISTIHDAM_VALUES.map((v) => ({ value: v, label: t(`hr.personel.form.options.istihdam.${v}`) }))}
              />
            )}
          />
        </FormField>
        <FormField label={t('hr.personel.fields.calisma')} required error={err('calisma_turu')}>
          <Controller
            control={control}
            name="calisma_turu"
            render={({ field }) => (
              <Select
                size="lg"
                value={field.value}
                onChange={field.onChange}
                aria-label={t('hr.personel.fields.calisma')}
                options={CALISMA_TURU_VALUES.map((v) => ({ value: v, label: t(`hr.personel.calisma.${v}`) }))}
              />
            )}
          />
        </FormField>
        <FormField label={t('hr.personel.detail.iseGiris')} htmlFor="f-giris" required error={err('ise_giris')}>
          <Controller
            control={control}
            name="ise_giris"
            render={({ field }) => (
              <DateField id="f-giris" className="h-10 rounded-xl" value={field.value} onChange={field.onChange} aria-invalid={!!err('ise_giris')} aria-label={t('hr.personel.detail.iseGiris')} />
            )}
          />
        </FormField>
        {time('calisma_baslangic', 'f-cb', t('hr.personel.form.workStart'))}
        {time('calisma_bitis', 'f-ce', t('hr.personel.form.workEnd'))}
      </FormSection>

      <FormSection title={t('hr.personel.form.sections.ucret')} icon={<Wallet />} description={t('hr.personel.form.hints.ucret')}>
        <FormField label={t('hr.personel.form.brutMaas')} htmlFor="f-maas" required error={err('brut_maas')}>
          <Controller
            control={control}
            name="brut_maas"
            render={({ field }) => (
              <NumberInput
                id="f-maas"
                className="h-10 rounded-xl"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                maxDecimals={2}
                minDecimalsOnBlur={2}
                suffix="TRY"
                placeholder="85.000,00"
                aria-invalid={!!err('brut_maas')}
              />
            )}
          />
        </FormField>
        <FormField label={t('hr.personel.form.maasGecerlilik')} htmlFor="f-maas-gec" error={err('maas_gecerlilik')} hint={t('hr.personel.form.hints.maasGecerlilik')}>
          <Controller
            control={control}
            name="maas_gecerlilik"
            render={({ field }) => (
              <DateField id="f-maas-gec" className="h-10 rounded-xl" value={field.value ?? ''} onChange={field.onChange} clearable aria-label={t('hr.personel.form.maasGecerlilik')} />
            )}
          />
        </FormField>
        <FormField label={t('hr.personel.fields.sigorta')} required error={err('sigorta')} hint={t('hr.personel.form.hints.sigorta')}>
          <Controller
            control={control}
            name="sigorta"
            render={({ field }) => (
              <Select
                size="lg"
                value={field.value}
                onChange={field.onChange}
                aria-label={t('hr.personel.fields.sigorta')}
                options={SIGORTA_VALUES.map((v) => ({ value: v, label: t(`hr.personel.form.options.sigorta.${v}`) }))}
              />
            )}
          />
        </FormField>
        {sigorta === 'emekli' ? (
          <>
            <FormField label={t('hr.personel.form.emeklilikTarihi')} htmlFor="f-emekli-tarih" required error={err('emeklilik_tarihi')} hint={t('hr.personel.form.hints.emeklilikTarihi')}>
              <Controller
                control={control}
                name="emeklilik_tarihi"
                render={({ field }) => (
                  <DateField id="f-emekli-tarih" className="h-10 rounded-xl" value={field.value ?? ''} max={new Date().toLocaleDateString('sv-SE')} onChange={field.onChange} aria-label={t('hr.personel.form.emeklilikTarihi')} />
                )}
              />
            </FormField>
            <FormField label={t('hr.personel.form.emeklilikNotu')} htmlFor="f-emekli-not" error={err('emeklilik_notu')} hint={t('hr.personel.form.hints.emeklilikNotu')}>
              <Input id="f-emekli-not" className="h-10 rounded-xl" maxLength={1000} placeholder={t('hr.personel.form.emeklilikNotuPh')} {...register('emeklilik_notu')} />
            </FormField>
          </>
        ) : null}
      </FormSection>

      <FormSection title={t('hr.personel.form.sections.ortaklik')} icon={<Handshake />}>
        <FormField label={t('hr.personel.form.ortak')} htmlFor="f-ortak" hint={t('hr.personel.form.hints.ortak')}>
          <label className="flex h-10 items-center gap-2 text-sm">
            <input id="f-ortak" type="checkbox" className="size-4 accent-primary" {...register('ortak')} />
            {t('hr.personel.form.ortakCheck')}
          </label>
        </FormField>
        {ortak ? (
          <FormField label={t('hr.personel.form.ortaklikPayi')} htmlFor="f-ortak-pay" error={err('ortaklik_payi')}>
            <Controller
              control={control}
              name="ortaklik_payi"
              render={({ field }) => (
                <NumberInput id="f-ortak-pay" className="h-10 rounded-xl" value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} maxDecimals={2} suffix="%" aria-invalid={!!err('ortaklik_payi')} />
              )}
            />
          </FormField>
        ) : null}
      </FormSection>
    </div>
  )
}
